"use client";

import { Play, Volume2, VolumeX } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { useAds } from "@/components/ads/Ads";
import { useUi } from "@/components/ui/use-ui";
import { PlayerStarted } from "@/components/video/Player";
import { cn } from "@/lib/cn";
import { fmt } from "@/lib/locales/fmt";
import { track } from "@/lib/stats-client";
import type { AdDevice } from "@/lib/site-defaults";

// The video ad before a video (Admin → Settings → pre-roll: ExoClick's in-stream zone, and any
// further network's tag for this device when the one before has none). The VAST tags are asked for in turn when
// the page opens, if this visitor is due one (at most every `everyMinutes`, not with
// Premium); once it's in, our play button starts the ad instead, in its own <video> over the player.
// It can be skipped when the ad allows (after 5 seconds if it doesn't say), a click on it opens the
// advertiser, and the network hears of the impression, the progress, the skip or the end. Then the
// player is as before: its play button starts the video (a framed video only plays from a click inside
// its frame). If the ad doesn't come, or the video was started first, nothing shows.

const SEEN_KEY = "sweet:ad-preroll";
/** How long to wait for the ad network before letting the video go without one. */
const VAST_TIMEOUT_MS = 6000;
/**
 * When the ad itself doesn't say when it can be skipped. ExoClick's zone is set to 5 seconds and passes
 * it on in the wrapped tag (skipOffset=…), which not every advertiser's VAST repeats.
 */
const DEFAULT_SKIP_SECONDS = 5;

type Tracker = import("@dailymotion/vast-client").VASTTracker;
type Ad = { fileUrl: string; skipAfter: number; tracker: Tracker; network: string };
type Phase = "loading" | "ready" | "playing" | "done";

/** The ad network a VAST tag belongs to, as the counters name it (lib/stats.ts). */
function networkOf(url: string): string {
  const host = new URL(url).hostname;
  if (host.endsWith("magsrv.com")) return "exoclick";
  return host.split(".").slice(-2, -1)[0]?.replace(/[^a-z0-9]/g, "") || "other";
}

/** The device as the ad networks split their spots: a tablet (iPads ask as Macs with touch), a phone, else a computer. */
function deviceKind(): AdDevice {
  const ua = navigator.userAgent;
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "tablet";
  return /Mobi|iPhone|iPod|Android/i.test(ua) ? "mobile" : "desktop";
}

const due = (everyMinutes: number) => {
  try {
    return Date.now() - (Number(localStorage.getItem(SEEN_KEY)) || 0) >= everyMinutes * 60_000;
  } catch {
    return true;
  }
};

export function Preroll() {
  const t = useUi();
  const { preroll } = useAds();
  const started = useContext(PlayerStarted);
  const [phase, setPhase] = useState<Phase>("loading");
  const [ad, setAd] = useState<Ad | null>(null);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(0);
  const [muted, setMuted] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const impressed = useRef(false);

  // Ask for an ad once we know the visitor is due one.
  useEffect(() => {
    // Not due: nothing is asked for, and nothing shows.
    if (!preroll || phase !== "loading" || !due(preroll.everyMinutes)) return;
    let alive = true;
    (async () => {
      const { VASTClient, VASTTracker } = await import("@dailymotion/vast-client");
      const client = new VASTClient(0, 0);
      const device = deviceKind();
      // Each network in turn (those for this device) until one has an ad for this view.
      for (const tag of preroll.vastTags.filter((t) => !t.devices.length || t.devices.includes(device))) {
        if (!alive) return;
        const network = networkOf(tag.url);
        track("preroll", `${network}:request`);
        const res = await client.get(tag.url, { withCredentials: true, wrapperLimit: 5, timeout: VAST_TIMEOUT_MS }).catch(() => null);
        for (const vastAd of res?.ads ?? []) {
          for (const creative of vastAd.creatives ?? []) {
            if (creative.type !== "linear") continue;
            const files = (creative.mediaFiles ?? []).filter(
              (f: { fileURL?: string; mimeType?: string }) => f.fileURL?.startsWith("https://") && /^video\/(mp4|webm)$/.test(f.mimeType ?? ""),
            );
            const file = files.find((f: { mimeType?: string }) => f.mimeType === "video/mp4") ?? files[0];
            if (!file) continue;
            const tracker = new VASTTracker(client, vastAd, creative);
            tracker.on("clickthrough", (url: string) => url && window.open(url, "_blank", "noopener"));
            const skip = typeof creative.skipDelay === "number" && creative.skipDelay >= 0 ? creative.skipDelay : DEFAULT_SKIP_SECONDS;
            if (alive) {
              setAd({ fileUrl: file.fileURL, skipAfter: skip, tracker, network });
              setPhase("ready");
            }
            return;
          }
        }
      }
      if (alive) setPhase("done");
    })().catch(() => alive && setPhase("done"));
    return () => {
      alive = false;
    };
  }, [preroll, phase]);

  // The video was started before the ad came: no ad this time.
  if (!preroll || !ad || (phase !== "ready" && phase !== "playing") || (started && phase === "ready")) return null;

  const finish = (how: "complete" | "skip" | "error") => {
    if (how === "complete") ad.tracker.complete();
    else if (how === "skip") ad.tracker.skip();
    else ad.tracker.errorWithCode("405");
    track("preroll", `${ad.network}:${how}`);
    video.current?.pause();
    setPhase("done");
  };

  const start = () => {
    const el = video.current;
    if (!el) return setPhase("done");
    setPhase("playing");
    // Inside the click, so the ad may play with sound.
    el.play().catch(() => {
      el.muted = true;
      setMuted(true);
      el.play().catch(() => finish("error"));
    });
  };

  const left = Math.max(0, Math.ceil(ad.skipAfter - time));

  return (
    // Until our play button is pressed, the video's poster shows through and the ad isn't downloaded
    // (most visitors who open a page never press play).
    <div className={cn("theme-dark absolute inset-0 z-30", phase === "playing" && "bg-black")} data-ad="preroll">
      <video
        ref={video}
        src={ad.fileUrl}
        preload="none"
        playsInline
        className={cn("size-full object-contain", phase === "ready" && "opacity-0")}
        onClick={() => {
          if (phase !== "playing") return;
          ad.tracker.click();
          track("preroll", `${ad.network}:click`);
        }}
        onLoadedMetadata={(e) => {
          setLength(e.currentTarget.duration);
          ad.tracker.setDuration(e.currentTarget.duration);
        }}
        onPlaying={() => {
          if (impressed.current) return ad.tracker.setPaused(false);
          impressed.current = true;
          ad.tracker.trackImpression();
          track("preroll", `${ad.network}:impression`);
          try {
            localStorage.setItem(SEEN_KEY, String(Date.now()));
          } catch {}
        }}
        onPause={() => phase === "playing" && ad.tracker.setPaused(true)}
        onTimeUpdate={(e) => {
          setTime(e.currentTarget.currentTime);
          ad.tracker.setProgress(e.currentTarget.currentTime);
        }}
        onEnded={() => finish("complete")}
        onError={() => finish("error")}
      />

      {phase === "ready" ? (
        <button type="button" aria-label={t.player.play} onClick={start} className="absolute inset-0 grid place-items-center">
          <span className="glass grid size-[72px] place-items-center rounded-full text-white transition-transform hover:scale-105 md:size-20">
            <Play size={30} fill="currentColor" className="translate-x-0.5" />
          </span>
        </button>
      ) : (
        <>
          <span className="pointer-events-none absolute left-3 top-3 rounded-md bg-black/60 px-2 py-1 text-[12px] font-medium text-white/90">
            {t.ads.ad}
            {length ? ` · ${Math.max(0, Math.ceil(length - time))}s` : ""}
          </span>
          <button
            type="button"
            aria-label={muted ? t.ads.unmuteAd : t.ads.muteAd}
            onClick={() => {
              if (!video.current) return;
              video.current.muted = !muted;
              setMuted(!muted);
            }}
            className="absolute bottom-3 left-3 grid size-10 place-items-center rounded-full bg-black/60 text-white/90 hover:text-white"
          >
            {muted ? <VolumeX size={19} /> : <Volume2 size={19} />}
          </button>
          {left > 0 ? (
            <span className="absolute bottom-3 right-3 rounded-full bg-black/70 px-4 py-2.5 text-[13px] text-white/80">{fmt(t.ads.skipIn, { n: left })}</span>
          ) : (
            <button
              type="button"
              onClick={() => finish("skip")}
              className="absolute bottom-3 right-3 rounded-full bg-black/75 px-4 py-2.5 text-[13px] font-medium text-white ring-1 ring-white/30 hover:bg-black/90"
            >
              {t.ads.skipAd}
            </button>
          )}
          {length > 0 && (
            <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 bg-white/15">
              <span className="block h-full bg-[#f6c453]" style={{ width: `${Math.min(100, (100 * time) / length)}%` }} />
            </span>
          )}
        </>
      )}
    </div>
  );
}
